import { useEffect, useState } from 'react';

interface ReportProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (issue: string) => void;
}

export function ReportProblemModal({ isOpen, onClose, onSubmit }: ReportProblemModalProps) {
  const [issue, setIssue] = useState('');

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIssue('');
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-in fade-in duration-300" 
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative bg-white rounded-[20px] shadow-2xl w-full max-w-[360px] overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-1">
                Report Problem
              </h2>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px]">
                Please share your problem
              </p>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f4f4f4] transition-colors -mt-1 -mr-1"
            >
              <span className="material-icons text-[#3a3335] text-[20px]">close</span>
            </button>
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-6" />

          {/* Form Content */}
          <div className="mb-8">
            <label className="block font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-3">
              Please describe the issue
            </label>
            <textarea
              autoFocus
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
              placeholder="Write something..."
              className="w-full h-[140px] p-4 rounded-[12px] border border-[#ede8e9] font-['DM_Sans',sans-serif] text-[15px] text-[#3a3335] placeholder:text-[#908e8e] focus:outline-none focus:border-[#8e2a2f] resize-none transition-colors"
            />
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-6" />

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-[40px] bg-[#eee] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-sm hover:bg-[#e2e2e2] transition-all active:scale-[0.98] flex items-center justify-center"
            >
              Cancel
            </button>
            <button
              onClick={() => onSubmit(issue)}
              disabled={!issue.trim()}
              className="flex-1 h-[40px] bg-[#fcfaf7] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-sm hover:bg-[#f2eee2] transition-all active:scale-[0.98] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
